import React from 'react';

import {
  Popover as MuiPopover,
  PopoverProps as MuiPopoverProps,
  Box,
  IconButton,
  styled,
  useTheme,
  alpha,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { useWindowDimensions } from '@/hooks/useWindowDimensions';

interface PopoverProps extends MuiPopoverProps {
  onClose: () => void;
}

const MobilePopover = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  position: 'fixed',
  width: '100vw',
  height: '100vh',
  top: 0,
  left: 0,
  zIndex: 1000,
  fontSize: 18,
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 80,
  paddingBottom: 60,
  overflow: 'auto',
}));

const Popover = ({ children, open, ...props }: PopoverProps): JSX.Element | null => {
  const { width, height } = useWindowDimensions();
  const theme = useTheme();

  if (!open) { return null; }

  if (width <= 600) {
    return (
      <MobilePopover>
        {children}
        <IconButton
          onClick={props.onClose}
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </MobilePopover>
    );
  }

  // NOTE: We use anchorReference="anchorPosition" instead of anchorEl={document.body} to ensure
  // the popover is always centered in the viewport. Previously, using document.body as anchor
  // with anchorOrigin: { vertical: 'center' } caused the popover to shift down when page content
  // grew (e.g., adding stepper components), because it centered relative to the body's height,
  // not the viewport. With anchorPosition set to the viewport center and transformOrigin: 'center',
  // the popover stays centered regardless of page content size, and the animation originates
  // from the center of the screen.
  return (
    <MuiPopover
      {...props}
      style={{
        background: alpha(theme.palette.grey[900], 0.6),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={{ top: height / 2, left: width / 2 }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ p: 4, maxWidth: 560, flex: 1 }}>
        {children}
        <IconButton
          onClick={props.onClose}
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </Box>
    </MuiPopover>
  );
};

export default Popover;
